/**
 * Copyright (c) 2023, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { Container, ThemeProvider, createTheme } from "@mui/material";
import React from "react";
import "./App.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Footer from "./Containers/Footer";
import Header from "./Containers/Header";
import { CloudContextProvider, useCloud } from "./Hooks/Cloud";
import { HeaderContextProvider } from "./Hooks/Header";
import { RegionContextProvider } from "./Hooks/Region";
import { ShowRegionContextProvider } from "./Hooks/ShowRegion";
import { ShowSubscribeFeedContextProvider } from "./Hooks/ShowSubscribeFeed";
import History from "./Pages/History";
import Incident from "./Pages/Incident";
import Index from "./Pages/Index";
import NotFound from "./Pages/NotFound";

function App() {
  return (
    <CloudContextProvider>
      <RegionContextProvider>
        <HeaderContextProvider>
          <ShowRegionContextProvider>
            <ShowSubscribeFeedContextProvider>
              <AppContent />
            </ShowSubscribeFeedContextProvider>
          </ShowRegionContextProvider>
        </HeaderContextProvider>
      </RegionContextProvider>
    </CloudContextProvider>
  );
}

function AppContent() {
  const { cloud } = useCloud();
  const theme = createTheme(window.ENV[cloud].theme);
  const features = window.ENV[cloud].features;

  return (
    <div className="App">
      <ThemeProvider theme={ theme }>
        <Container maxWidth="100%">
          <Header />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={ <Index /> } />
              { features.history && <Route path="/history" element={ <History /> } /> }
              { features.incident && <Route path="/incidents/:id" element={ <Incident /> } /> }
              <Route path="*" element={ <NotFound isCurrentStatusVisible={ true } /> } />
            </Routes>
          </BrowserRouter>
          <Footer />
        </Container>
      </ThemeProvider>
    </div>
  );
}

export default App;
